import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

export default observer(function EditEmail() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        generalStore: { myAccount },
        modalStore: { showModal }
    } = useStore();

    // function
    const onChagngeEmail = () => {
        const idModal: string = 'change-email-modal';
        showModal({
            id: idModal,
            title: t('words_title.change_email'),
            size: 'md',
            titleClassName: 'text-center max-w-none',
            content: (
                <ChangeEmailForm idModal={idModal} />
            ),
            footerClassName: 'hidden'
        });
    };

    return (
        <div className='flex justify-between items-center gap-3'>
            <div className='break-all flex-1'>
                <Label className='mb-2 !leading-[1]'>{t('field.email')}</Label>
                <div>{myAccount?.email || '--'}</div>
            </div>
            <ButtonCus
                type='button'
                size='fit'
                variant='outline'
                className='w-fit gap-1.5 h-fit xsm:h-9.5 md:h-12 py-1 px-2 md:px-4 leading-lh-sma14 whitespace-nowrap'

                onClick={() => onChagngeEmail()}
            >
                <Trans
                    i18nKey='button.change_email'
                    components={{
                        br: <br className='block xsm:hidden' />
                    }}
                />
            </ButtonCus>
        </div>
    );
});

// #region  Change Email Form
const ChangeEmailForm = observer(({ idModal }: { idModal: string }) => {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        generalStore: { myAccount },
        authStore: { changeEmailEnduser, clearAuthentication },
        modalStore: { hideAllModals }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            email: ''
        }
    });

    // lifeCycle
    useEffect(() => {
        form.reset({ email: myAccount?.email ?? '' });
    }, [myAccount?.email]);

    // function
    const onSubmit = async (data: FormData) => {
        const res = await flowResult(changeEmailEnduser({
            id: Number(myAccount?.id),
            email: data?.email ?? ''
        }));

        if (res) {
            form.reset();
            hideAllModals();
            clearAuthentication();
        }
    };

    const onCancel = () => {
        hideAllModals();
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='mt-0'
                autoComplete='off'
            >
                <div className='flex flex-col gap-6'>
                    <FormField
                        control={form.control}
                        name='email'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.email')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('field.email')}
                                        isHalfWidth
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className='flex gap-4 mt-2 justify-center'>
                        <ButtonCus
                            variant='outline'
                            size='inModal'
                            type='button'
                            onClick={onCancel}
                        >
                            {t('button.cancel')}
                        </ButtonCus>
                        <ButtonCus
                            variant='default'
                            size='inModal'
                            type='submit'
                        >
                            {t('button.change')}
                        </ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
// #endregion
